import { useEffect, useState } from 'react';
import { ExpandableMilestoneCard } from '../../../../components/ExpandableMilestoneCard';
import { InlineMilestoneForm } from '../../../../components/InlineMilestoneForm';
import { Card, CardContent, CardDescription, CardHeader } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Progress } from '../../../../components/ui/progress';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { FileText, TrendingUp, CheckCircle, Clock, DollarSign, Sparkles } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { useSelector } from 'react-redux';
import { selectUserDetails } from 'slices/userProfile/reducer';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { cleanMoney } from 'components/AddMilestoneDialog';

interface Activity {
  id: string;
  timestamp: string;
  user: string;
  role: 'contractor' | 'homeowner';
  action: string;
  details?: string;
}

interface Milestone {
  id: string;
  name: string;
  percentage: number;
  amount: number;
  status: 'pending' | 'in-progress' | 'completed' | 'paid';
  description?: string;
  workStage: 'not-started' | 'started' | 'rough' | 'completed';
  inspectionImages: string[];
  inspectionStatus: 'pending' | 'approved' | 'disapproved';
  inspectionDisapprovalReason?: string;
  activities: Activity[];
  dueDate?: string;
}

export default function NewNonEscrow({project,gotoProjectPage,isEscrowProject,isEditDisable,setLoading,setRefresh,setContractValue,contractValue,handleMilestoneSubmit,milestones,setMilestones}:{project:any;isEditDisable:any; gotoProjectPage:any;setLoading:any;milestones:any;isEscrowProject:any; setRefresh:any;setMilestones:any;handleMilestoneSubmit:any,contractValue:any;setContractValue:any}) {
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMilestoneId, setPaymentMilestoneId] = useState<string | null>(null);
  const user = useSelector(selectUserDetails)
  const currentRole = user[0]?.user_type
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [percentage, setPercentage] = useState('');
  const [description, setDescription] = useState('');
  const [milestoneId, setMilestoneId] = useState('');

  const milestonePercentageTotal = Number(milestones.reduce(
    (total: number, milestone: any) => total + (Number(milestone.percentage) || 0),
    0
  ).toFixed(2));
  const milestonePercentageRemaining = Number(Math.max(0, 100 - milestonePercentageTotal).toFixed(2));
  const milestonePercentageOverage = Number(Math.max(0, milestonePercentageTotal - 100).toFixed(2));
  const editingMilestone = milestones.find((milestone: any) => String(milestone.id) === String(milestoneId));
  const maximumEditablePercentage = Number(Math.min(
    100,
    milestonePercentageRemaining + (Number(editingMilestone?.percentage) || 0)
  ).toFixed(2));

  const {t} = useTranslation()
  const handleAddMilestone = (newMilestone: { name: string; percentage: number; description: string ,id?:string}) => {
   const percentageWithoutCurrent = milestonePercentageTotal - (Number(editingMilestone?.percentage) || 0);
   if (newMilestone.percentage <= 0 || percentageWithoutCurrent + newMilestone.percentage > 100) {
    const availablePercentage = Number(Math.max(0, 100 - percentageWithoutCurrent).toFixed(2));
    toast.error(`Milestone percentage cannot exceed ${availablePercentage}% available.`);
    return false;
   }
   if(milestoneId){
    const activity: Activity = {
      id: Date.now().toString() + '-1',
      timestamp: new Date().toISOString(),
      user: currentRole === 'contractor' ? 'Contractor' : currentRole === 'homeowner' ? 'Homeowner' : 'System',
      role: currentRole || 'contractor',
      action: 'Updated milestone'
    };
setMilestones((m:any)=>{
  return m.map((mil:any)=>{
     if(mil.id ===milestoneId){
      return {
        ...mil,
        name: newMilestone.name,
        percentage: newMilestone.percentage,
	      description: newMilestone.description,
	      amount: cleanMoney(contractValue) * (newMilestone.percentage / 100),
      activities:mil.activities[0]? [...mil.activities,activity]:[activity],

      }
     }else{
      return mil
     }

  })
})
setMilestoneId("")
    toast.success('✨ Milestone updated successfully!');
   }else{
    const activity: Activity = {
      id: Date.now().toString() + '-1',
      timestamp: new Date().toISOString(),
      user: currentRole === 'contractor' ? 'Contractor' : currentRole === 'homeowner' ? 'Homeowner' : 'System',
      role: currentRole || 'contractor',
      action: 'Created milestone'
    };

    const milestone: Milestone = {
      id:`t-${Date.now().toString()}` ,
      name: newMilestone.name,
      percentage: newMilestone.percentage,
      amount: cleanMoney(contractValue)* (newMilestone.percentage / 100),
      status: 'pending',
      description: newMilestone.description,
      workStage: 'not-started',
      inspectionImages: [],
      inspectionStatus: 'pending',
      activities: [activity],
    };
    setMilestones([...milestones, milestone]);
    toast.success('✨ Milestone added successfully!');
   }
   return true;

  };

  const handleUpdateMilestone = (updated: Milestone) => {
    setMilestones(milestones.map((m:any)=> m.id === updated.id ? updated : m));
  };


  const handlePaymentClick = (id: string) => {
    setPaymentMilestoneId(id);
    setShowPaymentDialog(true);
  };


  const handlePayment = () => {
    if (paymentMilestoneId) {
      const activity: Activity = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        user: 'Homeowner',
        role: 'homeowner',
        action: 'Payment processed',
        details: `$${milestones.find((m:any) => m.id === paymentMilestoneId)?.amount.toLocaleString()}`
      };

      setMilestones(milestones.map((m:any) => 
        m.id === paymentMilestoneId 
          ? { ...m, status: 'paid' as const, activities: [...m.activities, activity] } 
          : m
      ));
      toast.success('🎉 Payment processed successfully!');
      setShowPaymentDialog(false);
      setPaymentMilestoneId(null);
    }
  };

  const handleContractValueChange = (value: number) => {
    setContractValue(value);
    setMilestones(milestones.map((m:any) => ({
      ...m,
      amount:cleanMoney(value)  * (m.percentage / 100),
    })));
  };
  const totalPaid = milestones.filter((m:any) => m.status === 'paid').reduce((sum:any, m:any) => sum + m.amount, 0);
  const totalPending = milestones.filter((m:any) => m.status !== 'paid').reduce((sum:any, m:any) => sum + m.amount, 0);
  const completedMilestones = milestones.filter((m:any) => m.status === 'completed' || m.status === 'paid').length;
  const progressPercentage = milestones.length > 0 ? (completedMilestones / milestones.length) * 100 : 0;

  const paymentMilestone = milestones.find((m:any) => m.id === paymentMilestoneId);
const onEditMilestone = (milestone:any)=>{
  setMilestoneId(milestone.id)
  setName(milestone.name)
  setPercentage(milestone.percentage)
  setDescription(milestone.description)
  setIsOpen(true)
}
  return (
    <div className="tailwind-prefix milestone-workspace">
      <div className="">
        {/* Stats Cards */}
        {project?.p_status ==="LIVE"&&   <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Card className="border bg-white hover:shadow-md transition-shadow" style={{ borderColor: '#e5e7eb' }}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardDescription style={{ color: '#6b7280' }}>Total Contract</CardDescription>
                <div className="p-1.5 rounded" style={{ backgroundColor: '#f3f4f6' }}>
                  <FileText className="h-3.5 w-3.5" style={{ color: '#1f57d6' }} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-3">
              <div className="text-2xl" style={{ color: '#18202a' }}>
                ${contractValue.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card className="border bg-white hover:shadow-md transition-shadow" style={{ borderColor: '#e5e7eb' }}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardDescription style={{ color: '#6b7280' }}>Total Paid</CardDescription>
                <div className="p-1.5 rounded" style={{ backgroundColor: '#f3f4f6' }}>
                  <CheckCircle className="h-3.5 w-3.5" style={{ color: '#1f57d6' }} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-3">
              <div className="text-2xl" style={{ color: '#18202a' }}>
                ${totalPaid.toLocaleString()}
              </div>
              <div className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
                {((totalPaid / cleanMoney(contractValue) ) * 100).toFixed(1)}% of total
              </div>
            </CardContent>
          </Card>

          <Card className="border bg-white hover:shadow-md transition-shadow" style={{ borderColor: '#e5e7eb' }}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardDescription style={{ color: '#6b7280' }}>Remaining</CardDescription>
                <div className="p-1.5 rounded" style={{ backgroundColor: '#f3f4f6' }}>
                  <Clock className="h-3.5 w-3.5" style={{ color: '#6b7280' }} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-3">
              <div className="text-2xl" style={{ color: '#18202a' }}>
                ${totalPending.toLocaleString()}
              </div>
              <div className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
                Across {milestones.length - milestones.filter((m:any) => m.status === 'paid').length} milestones
              </div>
            </CardContent>
          </Card>

          <Card className="border bg-white hover:shadow-md transition-shadow" style={{ borderColor: '#e5e7eb' }}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardDescription style={{ color: '#6b7280' }}>Progress</CardDescription>
                <div className="p-1.5 rounded" style={{ backgroundColor: '#f3f4f6' }}>
                  <TrendingUp className="h-3.5 w-3.5" style={{ color: '#1f57d6' }} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-3">
              <div className="text-2xl" style={{ color: '#18202a' }}>
                {completedMilestones}/{milestones.length}
              </div>
              <Progress value={progressPercentage} className="mt-1.5 h-1.5" />
            </CardContent>
          </Card>
        </div>}
      

        {/* Contract Configuration */}
        <section className="milestone-config-section mb-3">
          <div className="milestone-config-section__heading">
            <div className="flex items-center gap-2 text-[15px] font-medium">
              <Sparkles className="h-4 w-4" style={{ color: '#1f57d6' }} />
              Contract Configuration
            </div>
          </div>
          <div className="milestone-config-section__field">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="contract-value" className="text-sm">Total Contract Value</Label>
                {currentRole ==="contractor" && Number(project?.p_escrow) === 1 && ToolTipFunc('homeowner-tooltip', "What is your profit margin?")}
              </div>
              {Number(project?.p_escrow) === 1 && (
                <div className="text-xs text-gray-500">{t("Your part of the total project cost")}</div>
              )}
                <div className="relative">
                <Input
  id="contract-value"
  type="text"
  inputMode="numeric"
  onFocus={(e:any) => e.target.select()}
  readOnly={isEditDisable || project?.p_status === "LIVE" || Number(project?.p_escrow) === 0}
  value={contractValue}
  onChange={(e: any) => {
    let value = e.target.value;
  
    // 1️⃣ Allow only numbers, comma, dot
    if (!/^[0-9.,]*$/.test(value)) return;
  
    // 2️⃣ Allow only one dot
    if ((value.match(/\./g) || []).length > 1) return;
  
    // 3️⃣ Allow only 2 decimals
    const dotIndex = value.indexOf(".");
    if (dotIndex !== -1) {
      const decimals = value.slice(dotIndex + 1);
      if (decimals.length > 2) return;
    }
  
    // 4️⃣ If value is exactly "0" or "0.0", reset typing (start fresh)
    if (value === "0" || value === "0.0") {
      e.target.value = "";
      handleContractValueChange(0);
      return;
    }
  
    // 5️⃣ Remove commas for state
  
  
    // 6️⃣ Update input as-is (no auto-format)
    e.target.value = value;
  
    // 7️⃣ Send clean numeric string
    handleContractValueChange(value);
  }}
  
  className="pl-3 border"
  style={{ borderColor: "#d1d5db" }}
/>


                </div>
            </div>
          </div>
        </section>

	        <div className="mb-3">
          <div className="mb-3">
            <div className="text-[15px] mb-0.5">Project Timeline</div>
            <div className="text-sm text-gray-600">
              Follow your project's progress through each milestone
            </div>
	          </div>
	          <div
	            className={`mb-3 rounded-lg border p-3 ${milestonePercentageTotal === 100 ? 'border-green-200 bg-green-50' : milestonePercentageTotal > 100 ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}
	          >
	            <div className="flex items-center justify-between gap-3 text-sm">
	              <span className="font-medium text-gray-800">Milestone Allocation</span>
	              <strong className={milestonePercentageTotal === 100 ? 'text-green-700' : milestonePercentageTotal > 100 ? 'text-red-700' : 'text-blue-700'}>
	                {milestonePercentageTotal}% / 100%
	              </strong>
	            </div>
	            <Progress value={Math.min(milestonePercentageTotal, 100)} className="mt-2 h-2" />
	            <div className={`mt-2 flex items-center gap-1.5 text-xs ${milestonePercentageTotal === 100 ? 'text-green-700' : milestonePercentageTotal > 100 ? 'text-red-700' : 'text-gray-600'}`}>
	              {milestonePercentageTotal === 100 ? (
	                <><CheckCircle className="h-3.5 w-3.5" /> Complete. No additional milestone is required.</>
	              ) : milestonePercentageTotal > 100 ? (
	                <>Reduce milestone allocation by {milestonePercentageOverage}%.</>
	              ) : (
	                <><Clock className="h-3.5 w-3.5" /> {milestonePercentageRemaining}% remaining.</>
	              )}
	            </div>
	          </div>
	          {project?.p_status !=="LIVE" && !isEditDisable && (milestonePercentageTotal < 100 || Boolean(milestoneId)) && (
	            <InlineMilestoneForm
	              isEditDisable={isEditDisable}
	              isEscrowProject={isEscrowProject}
	              milestoneId={milestoneId}
	              isOpen={isOpen}
	              setIsOpen={setIsOpen}
	              name={name}
	              setName={setName}
	              percentage={percentage}
	              setPercentage={setPercentage}
	              description={description}
	              setDescription={setDescription}
	              onAdd={handleAddMilestone}
	              onCancel={() => setMilestoneId('')}
	              maxPercentage={maximumEditablePercentage || milestonePercentageRemaining}
	              totalContractValue={contractValue}
	            />
	          )}
         
        </div>

        {/* Milestone Journey */}
        {milestones.length === 0 ? (
          <Card className="p-12 text-center border-2 border-dashed border-gray-300">
            <div className="max-w-md mx-auto">
              <div className="p-4 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <FileText className="h-10 w-10 text-indigo-600" />
              </div>
              <h3 className="text-xl mb-2">No milestones yet</h3>
              <div className="text-gray-600 mb-6">
                Start building your project timeline by adding your first milestone
              </div>
            </div>
          </Card>
        ) : (
          <div className="space-y-0">
            {milestones.map((milestone:any, index:any) => (
              <ExpandableMilestoneCard
                key={milestone.id}
                isEscrowProject={isEscrowProject}
                project ={project}
                milestone={milestone}
                milestones={milestones}
                isEditDisable={isEditDisable}
                setMilestones={setMilestones}
                index={index}
                setRefresh={setRefresh}
                setLoading={setLoading}
                onEditMilestone={onEditMilestone}
                isLast={index === milestones.length - 1}
                onUpdate={handleUpdateMilestone}
                onPayClick={handlePaymentClick}
              />
            ))}
          </div>
        )}

        {/* Info Footer */}
        <div className="d-flex align-items-start gap-3 mt-4">
        <button onClick={()=>{
          gotoProjectPage(1)
        }} type="button" className="btn btn-light btn-label previestab">
          <i className="ri-arrow-left-line label-icon align-middle fs-lg me-2"></i>{" "}
         {t("Back to Project Detail")} 
        </button>
        <button
          type="button"
          className="btn btn-primary btn-label right ms-auto nexttab"
          onClick={() => {
            handleMilestoneSubmit()
          }}
        >
          <i className="ri-arrow-right-line label-icon align-middle fs-lg ms-2"></i>{" "}
       {t("Save & Continue to Contract")}
        </button>
      </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5" style={{ color: '#1f57d6' }} />
              Process Payment
            </DialogTitle>
            <DialogDescription className="text-sm">
              Complete the payment for this milestone
            </DialogDescription>
          </DialogHeader>
          
          {paymentMilestone && (
            <div className="py-4 space-y-4">
              <div className="p-4 rounded-lg border" style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}>
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <span className="text-xs" style={{ color: '#6b7280' }}>Milestone</span>
                    <span className="text-right text-sm" style={{ color: '#18202a' }}>{paymentMilestone.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs" style={{ color: '#6b7280' }}>Percentage</span>
                    <Badge variant="outline" className="text-xs">{paymentMilestone.percentage}%</Badge>
                  </div>
                  <div className="flex justify-between items-center border-t pt-2 mt-2" style={{ borderColor: '#e5e7eb' }}>
                    <span className="text-sm">Payment Amount</span>
                    <span className="text-xl" style={{ color: '#1f57d6' }}>
                      ${paymentMilestone.amount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="card-number" className="text-xs">Card Number</Label>
                  <Input 
                    id="card-number" 
                    placeholder="1234 5678 9012 3456"
                    className="mt-1 text-sm h-9"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="expiry" className="text-xs">Expiry Date</Label>
                    <Input 
                      id="expiry" 
                      placeholder="MM/YY"
                      className="mt-1 text-sm h-9"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cvv" className="text-xs">CVV</Label>
                    <Input 
                      id="cvv" 
                      placeholder="123" 
                      type="password"
                      maxLength={3}
                      className="mt-1 text-sm h-9"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="text-xs h-8">
              Cancel
            </Button>
            <Button 
              onClick={handlePayment}
              className="text-xs h-8"
              style={{ backgroundColor: '#1f57d6', color: 'white' }}
            >
              <DollarSign className="mr-1.5 h-3.5 w-3.5" />
              Pay ${paymentMilestone?.amount.toLocaleString()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </div>
  );
}
function ToolTipFunc(id:any,tip:any) {
  return <>
    <div
      data-tooltip-id={id}
      data-tooltip-content={tip}
      className="infoButton rounded-full text-center "
    >
      ?
    </div>

    <Tooltip id="homeowner-tooltip" place="top" />
  </>;
}
